import {
  EnvConfig,
  LIST_MENTIONABLE_USERS,
  useCurrentWorkspace,
  useFetcher,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataFunc, Mention, MentionsInput } from 'react-mentions';
import { StyleSheet, Text, View } from 'react-native';
import { Colors } from '../../themes';
import { AccountAvatar } from '../accountAvatar';
import './mentionsInputFieldWeb.css';

export type MentionsInputFieldProps = {
  inputFieldText: string;
  setInputFieldText: (text: string) => void;
  send: () => void;
  suggestionsPosition?: 'top' | 'bottom';
  handleEnter?: boolean;
  useCropCircle?: boolean; // Optional prop to control crop circle redesign
  placeholder?: string;
};

export const MentionsInputFieldWeb = (props: MentionsInputFieldProps) => {
  const { t } = useTranslation();
  const {
    inputFieldText,
    setInputFieldText,
    send,
    suggestionsPosition = 'top',
    handleEnter = true,
    useCropCircle,
    placeholder,
  } = props;

  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const fetchUsers: DataFunc = async (search, callback) => {
    const workspaceUsers = await queryClient.fetchQuery({
      queryKey: ['mentionableUsers', workspace?.id, search],
      queryFn: useFetcher(LIST_MENTIONABLE_USERS).bind(null, {
        filter: {
          workspaceId: workspace?.id,
          keyword: search,
        },
        page: {
          limit: 10,
          offset: 0,
        },
      }),
    });

    const results = workspaceUsers?.account?.listMentionableUsers?.items?.map(
      (u) => ({
        id: u.id,
        display: `${u.name.first} ${u.name.last}`,
        email: u.email,
        avatar: u.avatar,
      })
    );

    callback(results);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (!handleEnter) return;

    if (props.inputFieldText !== '' && e.code === 'Enter' && !e.shiftKey) {
      send();
      e.preventDefault();
      setInputFieldText('');
    }
  };

  useEffect(() => {
    if (portalRef.current) {
      setPortalNode(portalRef.current);
    }
  }, []);

  const renderMentionRow = (
    entry: any,
    search: string,
    highlightedDisplay: any,
    index: number,
    focused: boolean
  ) => {
    return (
      <div
        style={{
          ...mentionRowStyle,
          backgroundColor: focused ? Colors.blackWithAlpha : Colors.white,
          fontWeight: focused ? 'bold' : 'normal',
        }}
      >
        <AccountAvatar
          avatar={entry.avatar}
          mediaUrl={EnvConfig.mediaUrl}
          size={36}
        />
        <View
          style={{
            marginLeft: 16,
          }}
        >
          <Text style={styles.mentionRowName}>{highlightedDisplay}</Text>
          <Text style={styles.mentionRowEmail}>{entry.email}</Text>
        </View>
      </div>
    );
  };

  return (
    <div
      ref={portalRef}
      style={{
        width: '100%',
        height: useCropCircle ? '100%' : 64,
        overflow: 'visible',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      <MentionsInput
        value={inputFieldText}
        placeholder={placeholder || t('common.typeMessage')}
        style={getCustomStyle(suggestionsPosition, useCropCircle)}
        onChange={(e) => {
          setInputFieldText(e.target.value);
        }}
        spellCheck={false}
        onKeyPress={handleKeyPress}
        suggestionsPortalHost={portalNode || undefined}
      >
        <Mention
          trigger="@"
          markup="@[__display__](__id__)"
          appendSpaceOnAdd
          data={fetchUsers}
          renderSuggestion={renderMentionRow}
          displayTransform={(id, display) => `@${display}`}
          style={mentionStyle}
        />
      </MentionsInput>
    </div>
  );
};

const styles = StyleSheet.create({
  mentionRowName: {
    fontSize: 14,
    fontWeight: '400',
    letterSpacing: -0.25,
    color: Colors.mentionName,
  },
  mentionRowEmail: {
    fontSize: 12,
    fontWeight: '400',
    letterSpacing: 0.2,
    color: Colors.mentionEmail,
  },
});

const mentionRowStyle: any = {
  flex: 1,
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  height: 56,
  overflow: 'hidden',
  padding: '8px 16px',
  cursor: 'pointer',
};

const mentionStyle: any = {
  backgroundColor: Colors.primaryGrayBackground,
  color: Colors.primaryDark,
  position: 'relative',
  zIndex: 2,
  fontSize: '16px',
};

const getCustomStyle = (
  suggestionsPosition: 'top' | 'bottom' = 'top',
  useCropCircle?: boolean
): any => ({
  control: {
    fontSize: '16px',
    paddingLeft: '12px',
    width: '100%',
    height: useCropCircle ? '100%' : undefined,
    overflow: useCropCircle ? 'hidden' : undefined,
    position: 'relative',
    fontFamily: 'Inter, Helvetica Neue, Helvetica, Arial, sans-serif',
    fontWeight: '400',
  },
  highlighter: {
    overflow: 'hidden',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    width: '100%',
    height: useCropCircle ? '100%' : 60,
    paddingTop: useCropCircle ? '18px' : '8px',
    paddingBottom: useCropCircle ? '18px' : undefined,
    fontSize: '16px',
    boxSizing: 'border-box',
  },
  input: {
    height: useCropCircle ? '100%' : 60,
    paddingLeft: '12px',
    paddingTop: useCropCircle ? '18px' : '8px',
    paddingBottom: useCropCircle ? '18px' : undefined,
    width: useCropCircle ? '100%' : '96%',
    fontSize: '16px',
    outline: 'none',
    border: 'none',
    overflowY: 'auto',
    overflowX: 'hidden',
    className: 'mentions-input-textarea',
    backgroundColor: useCropCircle ? 'transparent' : Colors.white,
    borderRadius: undefined,
    boxSizing: 'border-box',
  },
  suggestions: {
    list: {
      flex: 1,
      position: 'absolute',
      overflow: 'auto',
      top: suggestionsPosition === 'bottom' ? '100%' : 'auto',
      bottom: suggestionsPosition === 'top' ? 20 : 'auto',
      width: 320,
      maxHeight: 280,
      minHeight: 56,
      borderRadius: 4,
      backgroundColor: Colors.white,
      left: -10,
      zIndex: 99999,
      boxShadow:
        suggestionsPosition === 'bottom'
          ? '0 4px 8px rgba(0, 0, 0, 0.2)'
          : '0 -4px 8px rgba(0, 0, 0, 0.2)',
    },
  },
});
